<template>
  <div class="version-wrapper">
    <button class="version-trigger" type="button" @click="openModal">
      <div class="version-show">
        <span>
          {{ $t("classroom.helicas.guide.versionLabel") }}
          <span v-if="versionDisplayText"> {{ versionDisplayText }}</span>
        </span>
      </div>
    </button>

    <div
      v-if="modalOpen"
      class="intro-layer version-layer"
      :class="stage ? `st-${stage}` : ''"
      role="dialog"
      aria-modal="true"
      @click.self="closeModal"
    >
      <div class="intro-center">
        <div class="intro-group">
          <div class="intro-avatar-shell" :class="stage ? `st-${stage}` : ''">
            <div class="intro-halo" aria-hidden="true"></div>
            <img class="intro-avatar" :src="avatarUrl" alt="Andrej" />
            <span
              v-for="n in 8"
              :key="`sp-${n}`"
              class="sparkle"
              :style="`--i:${n}`"
              aria-hidden="true"
            ></span>
          </div>

          <section
            class="intro-bubble version-bubble"
            :class="stage ? `st-${stage}` : ''"
          >
            <button
              class="guide-close intro-close"
              type="button"
              @click="closeModal"
              aria-label="Zavrieť okno verzie"
            >
              ✕
            </button>
            <header class="version-header">
              <div class="version-title">
                <h2 class="intro-title">Prehľad vydaných verzií</h2>
              </div>
              <div class="version-meta">
                <span v-if="versionText" class="version-badge">
                  Aktuálna verzia {{ versionText }}
                </span>
                <span v-else-if="apiLoading || apiError" class="version-badge">
                  {{ $t("classroom.helicas.guide.versionLoading") }}
                </span>
                <span v-if="latestReleaseDate" class="version-date">
                  Posledná aktualizácia: {{ latestReleaseDate }}
                </span>
              </div>
            </header>
            <p class="intro-lead version-lead">
              Scrolluj v okne a pozri si, čo sa menilo počas vývoja stránky.
            </p>
            <div class="version-panel">
              <aside class="version-nav" aria-label="Zoznam verzií">
                <button
                  v-for="(release, idx) in normalizedHistory"
                  :key="`nav-${idx}`"
                  class="version-nav-item"
                  :class="{ active: idx === activeIndex }"
                  type="button"
                  @click="setActiveIndex(idx)"
                >
                  <span class="nav-version">Verzia {{ release.version }}</span>
                  <span v-if="release.date" class="nav-date">
                    {{ release.date }}
                  </span>
                  <span v-if="release.summary" class="nav-summary">
                    {{ release.summary }}
                  </span>
                </button>
              </aside>
              <div
                class="version-detail"
                role="region"
                aria-label="Detail verzie"
              >
                <header class="release-head">
                  <p v-if="activeRelease.summary" class="release-summary">
                    {{ activeRelease.summary }}
                  </p>
                </header>
                <div
                  v-if="activeRelease.sections && activeRelease.sections.length"
                  class="release-sections"
                >
                  <div
                    v-for="(section, sIdx) in activeRelease.sections"
                    :key="`active-section-${sIdx}`"
                    class="release-section"
                  >
                    <h4 class="release-section-title">
                      {{ section.title }}
                    </h4>
                    <ul class="release-list">
                      <li
                        v-for="(item, itemIdx) in section.items"
                        :key="`active-item-${sIdx}-${itemIdx}`"
                      >
                        <span v-if="item.title" class="release-item-title">
                          {{ item.title }}
                        </span>
                        <span class="release-item-text">
                          {{ item.text }}
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import avatarGuideDefault from "@/assets/images/gallery/avatar-guide-tight.png";

export default {
  name: "VersionShow",
  props: {
    displayVersion: { type: String, default: "" },
    updatesInMonth: { type: Number, default: 1 },
    changes: {
      type: Array,
      default: () => [],
    },
    history: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      modalOpen: false,
      stage: "",
      closeTimer: null,
      activeIndex: 0,
      apiHistory: [],
      apiLoading: false,
      apiError: "",
    };
  },
  mounted() {
    this.loadHistory();
  },
  computed: {
    versionText() {
      if (this.displayVersion) return this.displayVersion;
      if (this.apiHistory?.length && this.apiHistory[0]?.version) {
        return this.apiHistory[0].version;
      }

      return "";
    },
    versionDisplayText() {
      if (this.versionText) return this.versionText;
      if (this.apiLoading || this.apiError) {
        return this.$t("classroom.helicas.guide.versionLoading");
      }
      return "";
    },
    normalizedHistory() {
      const source =
        Array.isArray(this.apiHistory) && this.apiHistory.length
          ? this.apiHistory
          : Array.isArray(this.history)
          ? this.history.filter(Boolean)
          : [];
      if (source.length) return source.map(this.normalizeRelease);

      const fallbackChanges = Array.isArray(this.changes)
        ? this.changes.filter(Boolean)
        : [];
      if (fallbackChanges.length) {
        return [
          this.normalizeRelease({
            version: this.versionText,
            date: "Aktuálna verzia",
            summary: "",
            sections: [
              {
                title: "Aktualizácie",
                items: fallbackChanges,
              },
            ],
          }),
        ];
      }

      return [];
    },
    latestReleaseDate() {
      return this.normalizedHistory[0]?.date || "";
    },
    activeRelease() {
      return (
        this.normalizedHistory[this.activeIndex] ||
        this.normalizedHistory[0] || {
          version: this.versionText,
          date: "",
          summary: "",
          sections: [],
        }
      );
    },
    avatarUrl() {
      return avatarGuideDefault;
    },
  },
  methods: {
    async loadHistory() {
      if (this.apiLoading) return;

      const apiBase = this.$store?.state?.api;
      if (!apiBase) return;

      this.apiLoading = true;
      this.apiError = "";
      try {
        const response = await fetch(`${apiBase}/history_updates/getData.php`, {
          method: "GET",
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const payload = await response.json();
        const status = String(payload?.status || "").toLowerCase();
        if (status !== "request succesfull" && status !== "success") {
          throw new Error(payload?.data || "Request failed");
        }
        const raw = Array.isArray(payload?.data) ? payload.data : [];
        const normalized = this.normalizeApiHistory(raw);
        if (normalized.length) {
          this.apiHistory = normalized;
          this.activeIndex = 0;
        }
      } catch (error) {
        this.apiError = error?.message || "Api error";
      } finally {
        this.apiLoading = false;
      }
    },
    normalizeApiHistory(raw) {
      const source = Array.isArray(raw) ? raw.filter(Boolean) : [];
      const sorted = source.slice().sort((a, b) => {
        const ad = String(a?.release_date || "");
        const bd = String(b?.release_date || "");
        const dateCompare = bd.localeCompare(ad);
        if (dateCompare !== 0) return dateCompare;
        return (Number(b?.id) || 0) - (Number(a?.id) || 0);
      });

      return sorted.map((release) => {
        const items = Array.isArray(release?.items) ? release.items : [];
        return {
          version: release?.version || "",
          date: this.formatReleaseDate(
            release?.release_date || release?.date || ""
          ),
          summary: release?.description || release?.summary || "",
          sections: this.buildSectionsFromItems(items),
        };
      });
    },
    buildSectionsFromItems(items) {
      const list = Array.isArray(items) ? items.slice() : [];
      list.sort((a, b) => {
        const orderA = Number(a?.sort_order) || 0;
        const orderB = Number(b?.sort_order) || 0;
        if (orderA !== orderB) return orderA - orderB;
        return (Number(a?.id) || 0) - (Number(b?.id) || 0);
      });

      const sectionsMap = new Map();
      for (const item of list) {
        const type = String(item?.item_type || item?.type || "")
          .toLowerCase()
          .trim();
        const text = String(item?.item_text || item?.text || "").trim();
        if (!text) continue;

        const title = this.sectionTitleForType(type);
        if (!sectionsMap.has(title)) sectionsMap.set(title, []);
        sectionsMap.get(title).push({ title: "", text });
      }

      const preferredOrder = [
        "Novinky",
        "Vylepšenia",
        "Opravy",
        "Odstránené",
        "Zmeny",
      ];
      const sections = [];
      for (const title of preferredOrder) {
        if (sectionsMap.has(title)) {
          sections.push({ title, items: sectionsMap.get(title) });
          sectionsMap.delete(title);
        }
      }

      const remainingTitles = Array.from(sectionsMap.keys()).sort((a, b) =>
        String(a).localeCompare(String(b))
      );
      for (const title of remainingTitles) {
        sections.push({ title, items: sectionsMap.get(title) });
      }

      return sections;
    },
    sectionTitleForType(type) {
      switch (type) {
        case "added":
          return "Novinky";
        case "improved":
          return "Vylepšenia";
        case "repaired":
          return "Opravy";
        case "removed":
          return "Odstránené";
        default:
          return "Zmeny";
      }
    },
    formatReleaseDate(dateStr) {
      if (!dateStr) return "";
      const parts = String(dateStr).split("-");
      if (parts.length === 3) {
        const year = Number(parts[0]);
        const month = Number(parts[1]);
        const day = Number(parts[2]);
        if (year && month && day) {
          return `${day}. ${month}. ${year}`;
        }
      }
      return dateStr;
    },
    normalizeItem(item) {
      if (typeof item === "string") return { title: "", text: item };
      if (item && typeof item === "object") {
        return {
          title: item.title || "",
          text: item.text || item.detail || item.desc || "",
        };
      }
      return { title: "", text: String(item || "") };
    },
    normalizeRelease(release) {
      if (!release || typeof release !== "object") {
        return {
          version: this.versionText,
          date: "",
          summary: "",
          sections: [],
        };
      }

      const rawSections = Array.isArray(release.sections)
        ? release.sections
        : [];
      const sections = rawSections.map((section) => {
        if (!section || typeof section !== "object") {
          return { title: "Zmeny", items: [this.normalizeItem(section)] };
        }
        const items = Array.isArray(section.items) ? section.items : [];
        return {
          title: section.title || "Zmeny",
          items: items.map(this.normalizeItem),
        };
      });

      return {
        version: release.version || this.versionText,
        date: release.date || "",
        summary: release.summary || "",
        sections,
      };
    },
    setActiveIndex(idx) {
      this.activeIndex = idx;
    },
    openModal() {
      if (this.modalOpen) return;
      this.stage = "";
      this.activeIndex = 0;
      this.modalOpen = true;
      requestAnimationFrame(() => {
        this.stage = "ready";
      });
    },
    closeModal() {
      if (!this.modalOpen) return;
      this.stage = "leave";
      if (this.closeTimer) clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => {
        this.modalOpen = false;
        this.stage = "";
      }, 360);
    },
  },
  beforeUnmount() {
    if (this.closeTimer) clearTimeout(this.closeTimer);
  },
};
</script>

<style lang="scss" scoped>
.version-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.version-trigger {
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  font: inherit;
  transition: transform 0.18s ease;
}

.version-trigger:focus-visible {
  outline: 2px solid #9abe4a;
  outline-offset: 2px;
  border-radius: 6px;
}

.version-show {
  font-size: inherit;
  color: #9abe4a;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  gap: 0.25em;
  transition: transform 0.18s ease, text-shadow 0.18s ease, color 0.18s ease;
}

.version-trigger:hover .version-show,
.version-trigger:focus-visible .version-show {
  transform: translateY(-2px) scale(1.02);
  text-shadow: 0 3px 12px rgba(154, 190, 74, 0.35);
  color: #a7ce55;
}

@media (prefers-reduced-motion: reduce) {
  .version-trigger,
  .version-show {
    transition: none;
  }
}

.version-bubble {
  text-align: left;
  --intro-bubble-w: clamp(22rem, 56vw, 50rem);
  --intro-avatar-w: clamp(12rem, 20vw, 16rem);
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  background: var(--ha-card-bg, #ffffff);
  color: var(--ha-card-fg, #2a2f1f);
  border: 1px solid var(--ha-card-border, rgba(138, 170, 68, 0.12));
  box-shadow: 0 1.1rem 3.2rem rgba(30, 40, 20, 0.12);
  font-family: inherit;
}

.version-header {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.version-title {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.version-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  font-size: 0.86rem;
  color: rgba(55, 65, 40, 0.78);
}

.version-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.22rem 0.7rem;
  border-radius: 999px;
  background: rgba(144, 202, 80, 0.2);
  color: #4a651c;
  font-weight: 700;
}

.version-date {
  font-weight: 600;
}

.version-lead {
  margin-bottom: 0.2rem;
}

.version-panel {
  display: grid;
  grid-template-columns: minmax(9rem, 12rem) minmax(0, 1fr);
  gap: 1rem;
  border-top: 1px solid rgba(154, 190, 74, 0.16);
  padding-top: 0.8rem;
  height: clamp(13rem, 46vh, 24rem);
}

.version-nav {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  height: 100%;
  overflow-y: auto;
  padding-right: 0.2rem;
  font-family: inherit;
}

.version-nav::-webkit-scrollbar {
  width: 0.35rem;
}

.version-nav::-webkit-scrollbar-thumb {
  background: rgba(144, 202, 80, 0.9);
  border-radius: 999px;
}

.version-nav-item {
  text-align: left;
  background: transparent;
  border: 1px solid rgba(144, 202, 80, 0.25);
  border-radius: 0.75rem;
  padding: 0.5rem 0.6rem;
  min-height: 4.6rem;
  max-height: 4.6rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.15rem;
  cursor: pointer;
  transition: border-color 0.18s ease, background 0.18s ease;
  color: inherit;
  font-family: inherit;
}

.version-nav-item:hover,
.version-nav-item:focus-visible {
  border-color: rgba(144, 202, 80, 0.85);
  background: rgba(144, 202, 80, 0.2);
}

.version-nav-item.active {
  border-color: #90ca50;
  background: rgba(144, 202, 80, 0.28);
  color: #24410f;
}

.nav-version {
  display: block;
  font-weight: 700;
  font-size: 0.95rem;
}

.nav-date {
  display: block;
  font-size: 0.78rem;
  color: rgba(60, 70, 48, 0.72);
}

.nav-summary {
  display: block;
  font-size: 0.82rem;
  color: rgba(55, 65, 40, 0.78);
  margin-top: 0.2rem;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.version-detail {
  height: 100%;
  overflow-y: auto;
  padding-right: 0.4rem;
  font-family: inherit;
  text-align: left;
}

.version-detail::-webkit-scrollbar {
  width: 0.45rem;
}

.version-detail::-webkit-scrollbar-thumb {
  background: rgba(144, 202, 80, 0.9);
  border-radius: 999px;
}

.version-detail::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.04);
  border-radius: 999px;
}

.release-head {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  margin-bottom: 0.45rem;
}

.release-date {
  display: inline-flex;
  align-items: center;
  padding: 0.18rem 0.5rem;
  border-radius: 999px;
  font-size: 0.8rem;
  color: rgba(60, 70, 48, 0.72);
  background: rgba(111, 125, 86, 0.1);
  font-weight: 600;
}

.release-summary {
  margin: 0;
  font-size: 0.92rem;
  color: rgba(55, 65, 40, 0.82);
  text-align: left;
}

.release-sections {
  display: grid;
  gap: 0.7rem;
}

.release-section-title {
  margin: 0 0 0.4rem;
  font-size: 0.9rem;
  font-weight: 800;
  color: rgba(70, 88, 38, 0.92);
  text-align: left;
}

.release-list {
  margin: 0;
  padding-left: 0;
  list-style: none;
  display: grid;
  gap: 0.35rem;
}

.release-list li {
  padding-left: 0;
}

.release-item-title {
  display: block;
  font-weight: 700;
  color: rgba(55, 65, 40, 0.92);
  text-align: left;
}

.release-item-text {
  display: block;
  color: rgba(55, 65, 40, 0.78);
  font-size: 0.9rem;
  text-align: left;
}

@media (max-width: 48rem) {
  .version-bubble {
    --intro-bubble-w: min(92vw, 28rem);
    --intro-avatar-w: clamp(10rem, 30vw, 13rem);
  }

  .version-panel {
    grid-template-columns: 1fr;
  }

  .version-nav {
    flex-direction: row;
    overflow-x: auto;
    overflow-y: hidden;
    max-height: none;
    padding-bottom: 0.4rem;
  }

  .version-nav-item {
    min-width: 13rem;
  }
}
</style>
